import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import axios from "axios";

const LOCK_FIELDS = [
    {
        name: "schedule_lock",
        label: "Schedule Lock",
        caption: "Prevents the schedule from being edited or regenerated.",
    },
    {
        name: "team_lock",
        label: "Team Lock",
        caption: "Prevents teams from being added, edited, or removed.",
    },
    {
        name: "player_lock",
        label: "Player Lock",
        caption: "Prevents players from registering or changing teams.",
    },
];

function EditSeasonLocksModal({ show, handleClose, season, onSeasonUpdated }) {
    const [formData, setFormData] = useState({
        schedule_lock: false,
        team_lock: false,
        player_lock: false,
    });

    const [loading, setLoading] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    useEffect(() => {
        if (show && season) {
            setFormData({
                schedule_lock: !!season.schedule_lock,
                team_lock: !!season.team_lock,
                player_lock: !!season.player_lock,
            });
            setErrorMessages([]);
        }
    }, [show, season]);

    const handleToggle = (field, label) => (e) => {
        const checked = e.target.checked;
        if (checked) {
            const confirmed = window.confirm(
                `Are you sure you want to enable ${label}? This may restrict changes for commissioners and users until it's turned off again.`
            );
            if (!confirmed) return;
        }
        setFormData((prev) => ({ ...prev, [field]: checked }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessages([]);

        axios
            .patch(`/api/leagues/seasons/${season.id}/settings/`, formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
            })
            .then((response) => {
                onSeasonUpdated(response.data);
                handleClose();
            })
            .catch((error) => {
                console.error("Error updating season locks:", error.response?.data);
                const data = error.response?.data || {};
                if (typeof data === "object") {
                    const parts = Object.entries(data).flatMap(([field, msgs]) =>
                        (Array.isArray(msgs) ? msgs : [msgs]).map((m) => `${field !== 'non_field_errors' ? `[${field}] ` : ''}${m}`)
                    );
                    setErrorMessages(parts.length ? parts : ["Failed to update locks."]);
                } else {
                    setErrorMessages(["Failed to update locks."]);
                }
            })
            .finally(() => setLoading(false));
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Season Locks</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessages.length > 0 && (
                    <Alert variant="danger">
                        {errorMessages.map((err, i) => (
                            <div key={i}>{err}</div>
                        ))}
                    </Alert>
                )}
                <Form onSubmit={handleSubmit}>
                    {LOCK_FIELDS.map(({ name, label, caption }) => (
                        <Form.Group className="mb-3" key={name}>
                            <Form.Check
                                type="switch"
                                id={`${name}-switch`}
                                label={label}
                                checked={formData[name]}
                                onChange={handleToggle(name, label)}
                            />
                            <Form.Text className="text-muted">{caption}</Form.Text>
                        </Form.Group>
                    ))}
                    <div className="text-end">
                        <Button variant="secondary" onClick={handleClose} className="me-2">
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : "Save Changes"}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

export default EditSeasonLocksModal;
